// Single source of truth for the panel look. Components get their styling from these antd tokens
// instead of `!important` overrides; tokens.css mirrors the same values as CSS variables for
// hand-written styles. Palette comes from the LeistFusion logo (slate ink + sky/teal).

export const colors = {
  brand: "#157f9f", // action color, AA with white text
  brandStrong: "#0f6f8c",
  brandDark: "#0e6580",
  brandDeep: "#13435a",
  brandInk: "#334a60",
  brand50: "#ecf7fb",
  brand100: "#d5eef6",
  brand400: "#4fb3d3",
  heading: "#13212c",
  text: "#1c2d3b",
  textSecondary: "#5a6f7e",
  textMuted: "#8597a4",
  border: "#d7e0e6",
  borderSoft: "#e5ebef",
  surface: "#ffffff",
  surface2: "#f8fafb",
  surfaceMuted: "#eef3f6",
  bg: "#f4f7f9",
  success: "#178a55",
  warning: "#a86a08",
  error: "#c93c30"
};

// App chrome (top bar, sider, nav drawer) sits on the logo navy. Mirrors --ds-chrome-* in tokens.css.
export const chrome = {
  bg: "#205a77",
  bgDeep: "#123a4f",
  popupBg: "#184a63",
  text: "rgba(255, 255, 255, 0.8)",
  textStrong: "#ffffff",
  textMuted: "rgba(255, 255, 255, 0.6)",
  hover: "rgba(255, 255, 255, 0.09)",
  active: "rgba(40, 190, 229, 0.2)",
  pressed: "rgba(40, 190, 229, 0.28)",
  accent: "#4ccbf0"
};

// Nested under the shell's sider and drawer: the sidebars keep `theme="light"` menus and only the
// colors switch to the navy chrome, so the light-theme metrics (radius, heights) still apply.
export const chromeMenuTheme = {
  components: {
    Menu: {
      itemColor: chrome.text,
      itemHoverColor: chrome.textStrong,
      itemHoverBg: chrome.hover,
      itemSelectedBg: chrome.active,
      itemSelectedColor: chrome.textStrong,
      itemActiveBg: chrome.pressed,
      subMenuItemSelectedColor: chrome.textStrong,
      groupTitleColor: chrome.textMuted,
      popupBg: chrome.popupBg,
      dangerItemColor: "#ff9d94",
      dangerItemHoverColor: "#ffb8b1",
      dangerItemSelectedBg: "rgba(255, 157, 148, 0.16)",
      dangerItemActiveBg: "rgba(255, 157, 148, 0.22)"
    }
  }
};

const fontFamily =
  "'Inter', 'IBM Plex Sans Arabic', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

const baseTheme = {
  token: {
    colorPrimary: colors.brand,
    colorInfo: colors.brand,
    colorSuccess: colors.success,
    colorWarning: colors.warning,
    colorError: colors.error,
    colorLink: colors.brandStrong,
    colorLinkHover: colors.brandDark,
    colorText: colors.text,
    colorTextHeading: colors.heading,
    colorTextSecondary: colors.textSecondary,
    colorTextTertiary: colors.textMuted,
    colorTextPlaceholder: colors.textMuted,
    colorBorder: colors.border,
    colorBorderSecondary: colors.borderSoft,
    colorBgLayout: colors.bg,
    colorFillAlter: colors.surface2,
    colorFillTertiary: colors.surfaceMuted,
    controlItemBgActive: colors.brand50,
    controlItemBgActiveHover: colors.brand100,
    controlItemBgHover: colors.surfaceMuted,
    controlOutline: "rgba(42, 157, 192, 0.24)",
    fontFamily,
    fontSize: 14,
    borderRadius: 10,
    borderRadiusLG: 16,
    borderRadiusSM: 8,
    borderRadiusXS: 6,
    controlHeight: 40,
    controlHeightLG: 46,
    controlHeightSM: 30,
    boxShadow: "0 4px 10px rgba(19, 33, 44, 0.06), 0 12px 28px rgba(19, 33, 44, 0.08)",
    boxShadowSecondary: "0 8px 20px rgba(19, 33, 44, 0.1), 0 20px 44px rgba(19, 33, 44, 0.12)",
    boxShadowTertiary: "0 1px 2px rgba(19, 33, 44, 0.05)",
    motionDurationMid: "0.2s",
    wireframe: false
  },
  components: {
    Layout: {
      headerBg: colors.surface,
      headerHeight: 68,
      headerPadding: "0 24px",
      siderBg: colors.surface,
      bodyBg: colors.bg,
      triggerBg: colors.surface,
      triggerColor: colors.textSecondary,
      triggerHeight: 48,
      lightSiderBg: colors.surface,
      lightTriggerBg: colors.surface,
      lightTriggerColor: colors.textSecondary
    },
    Menu: {
      itemBg: "transparent",
      subMenuItemBg: "transparent",
      itemColor: colors.brandInk,
      itemHoverColor: colors.heading,
      itemHoverBg: colors.surfaceMuted,
      itemSelectedBg: colors.brand50,
      itemSelectedColor: colors.brandStrong,
      itemActiveBg: colors.brand100,
      groupTitleColor: colors.textMuted,
      itemHeight: 40,
      itemBorderRadius: 10,
      itemMarginInline: 12,
      itemMarginBlock: 2,
      iconSize: 17,
      collapsedIconSize: 18,
      activeBarBorderWidth: 0,
      // Dark tokens stay sensible in case a page renders a dark menu.
      darkItemBg: "transparent",
      darkSubMenuItemBg: "rgba(0, 0, 0, 0.14)",
      darkPopupBg: colors.brandDeep,
      darkItemSelectedBg: "rgba(79, 179, 211, 0.3)"
    },
    Table: {
      headerBg: colors.surface2,
      headerColor: colors.textSecondary,
      headerSplitColor: "transparent",
      headerBorderRadius: 0,
      rowHoverBg: "#f3f9fc",
      rowSelectedBg: colors.brand50,
      rowSelectedHoverBg: colors.brand100,
      borderColor: colors.borderSoft,
      cellPaddingBlock: 14,
      cellPaddingInline: 16,
      cellPaddingBlockSM: 8,
      cellPaddingInlineSM: 10
    },
    Button: {
      fontWeight: 600,
      primaryShadow: "0 1px 2px rgba(19, 33, 44, 0.08), 0 4px 12px rgba(21, 127, 159, 0.22)",
      defaultShadow: "0 1px 2px rgba(19, 33, 44, 0.05)",
      dangerShadow: "none",
      defaultBorderColor: colors.border,
      defaultColor: colors.text,
      paddingInline: 18
    },
    Input: { activeShadow: "0 0 0 3px rgba(42, 157, 192, 0.2)", hoverBorderColor: colors.brand400 },
    InputNumber: { activeShadow: "0 0 0 3px rgba(42, 157, 192, 0.2)" },
    Select: { optionSelectedBg: colors.brand50, optionSelectedColor: colors.brandStrong },
    DatePicker: { activeShadow: "0 0 0 3px rgba(42, 157, 192, 0.2)" },
    Card: {
      headerFontSize: 16,
      headerHeight: 56,
      paddingLG: 20,
      extraColor: colors.brandStrong
    },
    Form: {
      labelColor: colors.text,
      labelFontSize: 13,
      itemMarginBottom: 18,
      verticalLabelPadding: "0 0 6px"
    },
    Tabs: {
      inkBarColor: colors.brand,
      itemColor: colors.textSecondary,
      itemSelectedColor: colors.brandStrong,
      itemActiveColor: colors.brandDark,
      itemHoverColor: colors.heading,
      titleFontSize: 14,
      horizontalItemGutter: 24
    },
    Segmented: {
      itemSelectedColor: colors.brandStrong,
      trackBg: colors.surfaceMuted,
      borderRadius: 10
    },
    Tag: { defaultBg: colors.surfaceMuted, defaultColor: colors.textSecondary },
    Badge: { colorBgContainer: colors.surface },
    Tooltip: { colorBgSpotlight: colors.heading },
    Drawer: { paddingLG: 24 },
    Modal: { titleFontSize: 18, titleColor: colors.heading, headerBg: colors.surface },
    Pagination: { itemActiveBg: colors.brand50, itemSize: 34 },
    Statistic: { titleFontSize: 13, contentFontSize: 26 },
    Descriptions: { labelBg: colors.surface2, titleColor: colors.heading },
    Steps: { dotSize: 10 },
    Progress: { defaultColor: colors.brand, remainingColor: colors.surfaceMuted },
    Alert: { withDescriptionPadding: "16px 20px" },
    Empty: { colorTextDescription: colors.textSecondary }
  }
};

// Phones: bigger touch targets, and 16px text in fields so iOS Safari doesn't zoom on focus.
const touchFieldText = { fontSize: 16 };
const mobileTheme = {
  token: {
    ...baseTheme.token,
    controlHeight: 44,
    controlHeightLG: 50,
    controlHeightSM: 34
  },
  components: {
    ...baseTheme.components,
    Input: {
      ...baseTheme.components.Input,
      inputFontSize: 16,
      inputFontSizeLG: 16,
      inputFontSizeSM: 16
    },
    InputNumber: {
      ...baseTheme.components.InputNumber,
      inputFontSize: 16,
      inputFontSizeLG: 16,
      inputFontSizeSM: 16
    },
    Select: { ...baseTheme.components.Select, ...touchFieldText, optionHeight: 44 },
    DatePicker: {
      ...baseTheme.components.DatePicker,
      inputFontSize: 16,
      inputFontSizeLG: 16,
      inputFontSizeSM: 16
    },
    AutoComplete: touchFieldText,
    Cascader: touchFieldText,
    Mentions: touchFieldText,
    Menu: { ...baseTheme.components.Menu, itemHeight: 46, itemMarginInline: 10 },
    Tabs: { ...baseTheme.components.Tabs, horizontalItemGutter: 8 },
    Table: { ...baseTheme.components.Table, cellPaddingBlock: 10, cellPaddingInline: 12 },
    Card: { ...baseTheme.components.Card, paddingLG: 16, headerHeight: 52 }
  }
};

export const getAntdTheme = isMobile => (isMobile ? mobileTheme : baseTheme);

export const antdTheme = baseTheme;

// Categorical chart colors in fixed order (brand teal first). Validated for color-vision
// deficiency separation; several fall under 3:1 on white, so charts using them keep a legend
// or data labels.
export const chartColors = ["#157f9f", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#4a3aa7"];

// Shared ApexCharts look: recessive grid/axes, app font, no toolbar clutter.
export const apexBase = {
  chart: {
    fontFamily: "Inter, 'IBM Plex Sans Arabic', sans-serif",
    foreColor: colors.textSecondary,
    toolbar: { show: false },
    zoom: { enabled: false }
  },
  grid: { borderColor: colors.borderSoft, strokeDashArray: 4 },
  tooltip: { theme: "light" },
  legend: { fontSize: "13px", markers: { size: 6 }, itemMargin: { horizontal: 8, vertical: 4 } }
};
